import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';

import { formatSignedPoint } from '../common/resultViewModel';

const formatPoint = (value) => `${Number(value || 0).toLocaleString()}pt`;

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));

const buildPredictionLabel = (results) => {
  const labels = results
    .map((result) => result.label)
    .filter(Boolean);

  return labels.length > 0 ? labels.join(' / ') : null;
};

const buildRateLabel = (results) => {
  const rateLabels = results
    .map((result) => result.rateLabel)
    .filter((rateLabel) => rateLabel && rateLabel !== '-');

  return rateLabels.length > 0 ? `${rateLabels.join(' / ')}倍` : null;
};

const buildRankDisplay = (ranking) => {
  const currentRank = Number(ranking?.currentUserDisplayRank);
  if (!Number.isFinite(currentRank) || currentRank <= 0) {
    return null;
  }

  const previousRank = Number(ranking?.currentUserBeforeRank);
  if (!Number.isFinite(previousRank) || previousRank <= 0) {
    return {
      color: 'text.secondary',
      detail: null,
      main: `${currentRank}位`,
    };
  }

  const change = previousRank - currentRank;
  if (change === 0) {
    return {
      color: 'text.secondary',
      detail: '変動なし',
      main: `${currentRank}位`,
    };
  }

  return {
    color: change > 0 ? 'success.main' : 'error.main',
    detail: `${change > 0 ? '↑' : '↓'}${Math.abs(change)}`,
    main: `${previousRank}位 → ${currentRank}位`,
  };
};

function StatRow({ label, value }) {
  if (!value) return null;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight={800}
        sx={{ textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function HitResultContentPC({ viewModel }) {
  const { correctResults, ranking, summary } = viewModel;
  const predictionLabel = buildPredictionLabel(correctResults);
  const rateLabel = buildRateLabel(correctResults);
  const rankDisplay = buildRankDisplay(ranking);
  const winStreak = Number(ranking?.currentUserWinStreak);

  return (
    <Box sx={{ p: 2, maxWidth: 680, mx: 'auto' }}>
      <Stack spacing={2.25}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h3" fontWeight={900} sx={{ color: 'success.main' }}>
            的中！
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
            alignItems: 'center',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.light',
            bgcolor: 'rgba(46, 125, 50, 0.08)',
            p: 1.5,
            textAlign: 'center',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              払戻し
            </Typography>
            <Typography variant="h3" component="p" fontWeight={900} sx={{ lineHeight: 1.05, overflowWrap: 'anywhere' }}>
              {formatPoint(summary.totalRefund)}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              今回の収支
            </Typography>
            <Typography variant="h4" component="p" fontWeight={900} sx={{ color: summary.balance >= 0 ? 'success.main' : 'error.main', overflowWrap: 'anywhere' }}>
              {formatSignedPoint(summary.balance)}pt
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={800} sx={{ mb: 0.5 }}>
            あなたの予想
          </Typography>
          <Typography variant="h6" component="p" fontWeight={900} sx={{ overflowWrap: 'anywhere', lineHeight: 1.35 }}>
            {predictionLabel || '表示できません'}
          </Typography>
        </Box>

        <Divider />

        <Stack spacing={1}>
          <StatRow label="ベット額" value={hasDisplayValue(summary.totalBet) && Number(summary.totalBet) > 0 ? formatPoint(summary.totalBet) : null} />
          <StatRow label="ベット時レート" value={rateLabel} />
        </Stack>

        {rankDisplay ? (
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={800} sx={{ mb: 0.5 }}>
              ルーム内順位
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="h6" fontWeight={900}>
                {rankDisplay.main}
              </Typography>
              {rankDisplay.detail ? (
                <Typography variant="body1" fontWeight={900} sx={{ color: rankDisplay.color }}>
                  {rankDisplay.detail}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        {Number.isFinite(winStreak) && winStreak >= 2 ? (
          <Box>
            <Chip label={`${winStreak}連続的中`} color="success" sx={{ fontWeight: 900 }} />
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

export default HitResultContentPC;
